import { createStore, type StoreApi } from 'zustand/vanilla'
import type { SolvedSketch } from '../core/api'
import type { AssetFile } from '../files/assets'
import type { FileHandle } from '../files/file-service'
import type {
  BoundingBox,
  Diagnostic,
  DocHandle,
  Document,
  ElementDescriptor,
  FeatureManifest,
  FeatureStatus,
  Ghost,
  Orientation,
  OriginPlane,
  ParameterValue,
  Placement,
  PointRef,
  PrinterProfile,
  PrintReport,
  RegionInfo,
  SketchParams,
  Use,
} from '../generated/protocol'
import type { JsonSchema } from '../inspector/model'
import { startsNarrow } from '../layout/docking'
import { loadSettings, type Settings } from '../settings/settings'
import type { SketchToolId } from '../sketch/session'
import type { Camera, StandardView } from '../viewport/camera'
import type { SelectionItemKind } from '../viewport/picking'

// The app state (ui.md 2.8): one zustand vanilla store per app, so tests create isolated stores
// without React. Actions are the only code that changes it in response to the user.

export type AppContext = 'model' | 'sketch' | 'print'

export type AppState = {
  readonly core: CoreState
  readonly catalog: Catalog | null
  readonly printers: readonly PrinterProfile[]
  readonly document: DocumentState | null
  readonly context: AppContext
  readonly selection: readonly SelectionItem[]
  readonly hover: HoverTarget | null
  readonly command: ActiveCommand | null
  readonly sketch: SketchSession | null
  readonly print: PrintState
  readonly view: ViewState
  readonly panels: PanelsState
  readonly settings: Settings
  readonly notifications: readonly Notification[]
  /** The latest message for the polite live region, such as "Undid Rename" (3.20), or null. */
  readonly announcement: Announcement | null
  /** The hint about a refused pick, shown for 3 s (3.7, 3.16.1, 3.17), or null. */
  readonly pickHint: PickHint | null
  /** The feature type of the last feature command started in this session, which Repeat starts again (3.5); null before any. */
  readonly lastFeatureType: string | null
  /** The Keep sizes option of the units control (3.21): session state, on until the user turns it off. */
  readonly keepSizes: boolean
  /** The feature whose Problems row shows repair mode at rest (3.7, 3.9), or null; during a feature command repair mode follows the command. */
  readonly repair: RepairFocus | null
  /** What the last `show` action selected, with the cause's ghost it draws (4.2), or null; cleared when the selection changes. */
  readonly shown: ShownCause | null
}

/** The feature whose repairs repair mode shows (3.7); its current repair follows the active field. */
export type RepairFocus = { readonly feature: string }

/** A `show` action's feature and entity, and the ghost of the cause line it came from (null for a message action, 4.2). */
export type ShownCause = {
  readonly feature: string
  readonly entity: string | null
  readonly ghost: Ghost | null
}

export type CoreState = {
  readonly status: 'loading' | 'ready' | 'failed'
  readonly engineVersion: string | null
  readonly fatal: string | null
  /** Mutating requests sent and not yet answered. */
  readonly pendingMutations: number
  /** True while the core has answered nothing for `STILL_WORKING_SECONDS` with a waited-on request waiting (4.4; background requests never count); false at start. */
  readonly stillWorking: boolean
}

export type Catalog = {
  /** Entries in protocol order (sorted by type). */
  readonly features: readonly CatalogFeature[]
  readonly byType: Readonly<Record<string, CatalogFeature>>
}

export type CatalogFeature = {
  readonly type: string
  readonly manifest: FeatureManifest
  readonly paramsSchema: JsonSchema
}

export type DocumentState = {
  readonly documentHandle: DocHandle
  readonly name: string
  readonly file: FileReference | null
  readonly revision: number
  readonly recipe: Document
  /** The core's dirty flag from document_changed. */
  readonly dirty: boolean
  /** Set when the recipe was opened from a recovery copy or reopened after a core restart, so the core's baseline is not the file on disk; cleared by a successful save (3.19). */
  readonly localUnsaved: boolean
  /** The key of this document session's recovery entry (3.19): a new `crypto.randomUUID()` for each document the app adopts (New, Open, a recovery Open), kept when a core restart reopens the document (4.4). */
  readonly recoveryId: string
  /** The asset files the core accepted when this session opened the document (3.19; none after an `asset_mismatch` retry), which a core restart gives it again (4.4); empty when none. */
  readonly assets: readonly AssetFile[]
  readonly undoLabel: string | null
  readonly redoLabel: string | null
  readonly transactionOpen: boolean
  readonly focus: string | null
  readonly featureStatus: Readonly<Record<string, FeatureStatus>>
  readonly parameterValues: Readonly<Record<string, ParameterValue>>
  readonly diagnostics: readonly Diagnostic[]
  readonly sketches: Readonly<Record<string, SolvedSketch>>
  readonly datums: Readonly<Record<string, Placement | null>>
  /** Model-layer mesh metadata by body id; buffers live in the scene, not the store. */
  readonly meshes: Readonly<Record<string, MeshSummary>>
  readonly lastRebuildMilliseconds: number | null
  /** `bare_numbers` of the latest `document_changed`: expressions that are numbers without a unit (3.21). */
  readonly bareNumbers: number
  /** The ghost buffers of the latest `recomputed` (2.7), by buffer index. */
  readonly ghosts: Readonly<Record<number, Float32Array>>
  /** The latest `list_uses` result and the revision it describes, or null before the first (3.9). */
  readonly uses: UsesState | null
}

/** A `list_uses` result and the document revision it describes (3.9). */
export type UsesState = { readonly revision: number; readonly uses: readonly Use[] }

export type MeshSummary = {
  readonly meshId: number
  readonly boundingBox: BoundingBox
  readonly faceCount: number
  readonly edgeCount: number
  readonly vertexCount: number
}

/** The file a document was opened from or saved to: a web file handle or a desktop grant. */
export type FileReference = {
  readonly name: string
  readonly handle: FileHandle | null
  /** On the web, the folder the user chose that holds the file, through which the page reaches its `assets/` folder (3.19); null on the desktop and without one. */
  readonly folder: FileSystemDirectoryHandle | null
  /** `fileDigest` of the file's bytes as this session last read or wrote them, which Save compares to see a change made elsewhere (3.19); null without a handle or when unknown. */
  readonly digest: string | null
}

/** The kinds of selection items, declared once with the picking code that produces them. */
export type { SelectionItemKind }

export type SelectionItem = {
  /** Stable key (3.6), equal for the same thing across rebuilds. */
  readonly key: string
  readonly kind: SelectionItemKind
  /** Display label, for example "Face of Cube" or "Line e-1a2b". */
  readonly label: string
  readonly body: string | null
  /** Feature that owns the item: the sketch for sketch items, the datum, the feature itself, or, for faces, edges and vertices, `element.feature`. */
  readonly feature: string | null
  /** Element data for faces, edges and vertices (from pick_reference). */
  readonly element: ElementDescriptor | null
  /** Sketch data: the entity id, point ref, constraint id or region info. */
  readonly sketchItem: SketchItemReference | null
  /** Origin item name: XY, XZ, YZ, X, Y, Z or O. */
  readonly origin: OriginName | null
}

export type OriginName = 'XY' | 'XZ' | 'YZ' | 'X' | 'Y' | 'Z' | 'O'

/** The kinds of sketch entities the M1 UI draws and selects. */
export type SketchEntityType = 'point' | 'line' | 'circle' | 'arc'

export type SketchItemReference =
  | {
      readonly kind: 'entity'
      readonly sketch: string
      readonly entity: string
      readonly entityType: SketchEntityType
    }
  | { readonly kind: 'point'; readonly sketch: string; readonly point: PointRef }
  | { readonly kind: 'constraint'; readonly sketch: string; readonly constraint: string }
  | { readonly kind: 'region'; readonly sketch: string; readonly region: RegionInfo }
  | {
      readonly kind: 'origin'
      readonly sketch: string
      readonly which: 'point' | 'x_axis' | 'y_axis'
    }

export type HoverTarget = { readonly key: string; readonly kind: SelectionItemKind }

export type ActiveCommand = {
  readonly featureType: string
  /** Feature id: the new record's id, or the edited record's id. */
  readonly featureId: string
  readonly editing: boolean
  readonly title: string
  readonly params: Readonly<Record<string, unknown>>
  /** Path of the selection field that receives picks, or null. */
  readonly activeField: string | null
  /** Path of the selection field whose box the pointer is over, whose items the viewport highlights (3.7); absent or null for none. */
  readonly hoveredField?: string | null
  readonly fieldErrors: Readonly<Record<string, string>>
  /** Whether the feature record has been applied inside the transaction yet. */
  readonly applied: boolean
  /** Body this command creates (for op new, or for a body-creating target, 2.12) and the field it fills, if any. */
  readonly newBody: {
    readonly id: string
    readonly name: string
    readonly path: 'body' | 'target'
  } | null
  /** The helper sketch of "Placing holes on a face" (3.7), or null. */
  readonly helper: HelperSketch | null
  /** Paths the user changed in this command, least recent first, each listed once at its latest change (3.7: the value field, the cut direction). */
  readonly changedPaths: readonly string[]
  /** The state before each user change in this command, newest last (3.7, command history). */
  readonly history: readonly CommandStep[]
  /** Changes undone with Mod+Z, for Mod+Shift+Z, newest last. */
  readonly future: readonly CommandStep[]
  readonly preview: 'none' | 'pending' | 'shown' | 'failed'
}

/** A sketch record the command writes for holes placed on a face (3.7): one point entity per hole center, no constraints. */
export type HelperSketch = {
  readonly id: string
  readonly params: SketchParams
  /**
   * The face's placement in world coordinates (its `ElementDescriptor.placement`), on which the
   * helper's points are drawn, since `params.plane.fallback` is in body coordinates.
   */
  readonly placement: Placement
  /** Whether the record has been added inside the transaction. */
  readonly applied: boolean
}

/** The command state before one user change, with the label of the field the change touched. */
export type CommandStep = {
  readonly params: Readonly<Record<string, unknown>>
  readonly helper: HelperSketch | null
  readonly activeField: string | null
  readonly field: string
}

export type SketchSession = {
  /** Null while picking the plane for a new sketch, or for a sketch being re-attached. */
  readonly sketchId: string | null
  /** The sketch whose plane is being picked again (3.16.1, re-attaching), else null. */
  readonly reattaching: string | null
  readonly tool: SketchToolId | null
  readonly construction: boolean
  readonly showConstraints: boolean
  /** In-sketch selection (entities, points, constraints), separate from the app selection. */
  readonly selection: readonly SelectionItem[]
  readonly dragging: boolean
  /** Camera before enterSketch moved it, for leaving the sketch (3.16.11); null during plane picking. */
  readonly cameraBefore: Camera | null
  /**
   * The `entity_used` hint of the last Delete (3.16.10), shown in the sketch hint for 8 s or until
   * the next step prompt; absent or null for none.
   */
  readonly notice?: SketchNotice | null
}

/** A hint in place of the sketch's step prompt (3.16.10); each one is a new object. */
export type SketchNotice = { readonly message: string }

export type PrintState = {
  readonly report: PrintReport | null
  /** Revision the report describes; the badge is stale when it differs from the document revision. */
  readonly reportRevision: number | null
  /** Orientation the report was computed with (null as modeled); the badge is also stale when it differs from `orientation`. */
  readonly reportOrientation: Orientation | null
  readonly running: boolean
  readonly overhangMap: boolean
  readonly focusedFinding: FindingKey | null
  /** Session print orientation set by Lay on face (3.17); null prints as modeled. */
  readonly orientation: Orientation | null
  /** True while Lay on face waits for a face. */
  readonly pickingFace: boolean
}

/** A finding of the print report: the `BodyPrint.body` id, the check, and the finding's index in that check's `findings`. */
export type FindingKey = {
  readonly body: string
  readonly check: string
  readonly index: number
}

export type ViewState = {
  readonly projection: 'perspective' | 'orthographic'
  readonly named: StandardView | 'custom'
  readonly section: SectionState | null
  readonly filter: {
    readonly body: boolean
    readonly face: boolean
    readonly edge: boolean
    readonly vertex: boolean
  }
  /** Session-only visibility of sketches, datum planes and origin items, by selection key. */
  readonly sessionVisible: Readonly<Record<string, boolean>>
  /** Session-only locked bodies (not pickable). */
  readonly locked: readonly string[]
  /** Session-only: whether the ground and sketch grid is drawn (3.12). */
  readonly grid: boolean
  /** Minor spacing in mm of the grid drawn now, mirrored by the viewport controller; null when no grid is drawn (3.12, 3.21). */
  readonly gridSpacing: number | null
}

export type SectionState = {
  readonly plane: OriginPlane
  readonly offset: string
  readonly flipped: boolean
}

export type PanelsState = {
  readonly browserOpen: boolean
  readonly timelineCollapsed: boolean
  readonly paletteOpen: boolean
  readonly side: 'parameters' | 'health' | 'problems' | 'keys' | null
  readonly inspectorPosition: { readonly x: number; readonly y: number } | null
}

/** A button of a notification. */
export type NotificationAction = {
  readonly label: string
  readonly run: () => void
  /** True for a message's `apply` action, a document edit that waits during a feature command (3.7). */
  readonly editsDocument?: boolean
}

export type Notification = {
  readonly id: string
  readonly severity: 'info' | 'success' | 'warning' | 'error'
  readonly message: string
  readonly actions: readonly NotificationAction[]
  readonly createdAt: number
  /** The core's error or diagnostic code, or the id of a UI-owned message (4.3), shown as `data-code` (4.1). */
  readonly code?: string
  /** Feature ids a failure or loss notification names; it is dismissed once none of them is in error and none of its `problems` remains (4.2). Absent for other notifications. */
  readonly features?: readonly string[]
  /** The cause line shown under the message, in `textMuted` (3.22); absent when there is none. */
  readonly cause?: string
  /** Keys `{feature} {code} {field}` of the new losses a loss notification reports (4.2); absent for other notifications. */
  readonly problems?: readonly string[]
}

/** A live region message; `id` changes with every announcement, so a repeated text is read again. */
export type Announcement = { readonly id: number; readonly message: string }

/**
 * A hint about a pick the target refused: under the active command's selection field at `field`,
 * or in the sketch hint while picking a plane when `field` is null.
 */
export type PickHint = { readonly message: string; readonly field: string | null }

export type AppStore = StoreApi<AppState>

/** The page's localStorage, or null where reading it is not allowed. */
export function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    // Opaque origins and blocked site data throw on access.
    return null
  }
}

export const INITIAL_STATE: AppState = {
  core: {
    status: 'loading',
    engineVersion: null,
    fatal: null,
    pendingMutations: 0,
    stillWorking: false,
  },
  catalog: null,
  printers: [],
  document: null,
  context: 'model',
  selection: [],
  hover: null,
  command: null,
  sketch: null,
  print: {
    report: null,
    reportRevision: null,
    reportOrientation: null,
    running: false,
    overhangMap: true,
    focusedFinding: null,
    orientation: null,
    pickingFace: false,
  },
  view: {
    projection: 'perspective',
    named: 'iso',
    section: null,
    filter: { body: false, face: true, edge: true, vertex: true },
    sessionVisible: {},
    locked: [],
    grid: true,
    gridSpacing: null,
  },
  panels: {
    browserOpen: true,
    timelineCollapsed: false,
    paletteOpen: false,
    side: null,
    inspectorPosition: null,
  },
  settings: loadSettings(browserStorage()),
  notifications: [],
  announcement: null,
  pickHint: null,
  lastFeatureType: null,
  keepSizes: true,
  repair: null,
  shown: null,
}

/**
 * A store from `INITIAL_STATE` and the overrides. Without overridden panels, the browser starts
 * closed in a window narrower than 65rem at the stored UI scale, where it would float over the
 * free area (3.2).
 */
export function createAppStore(overrides: Partial<AppState> = {}): AppStore {
  const state = { ...INITIAL_STATE, ...overrides }
  const narrow = overrides.panels === undefined && startsNarrow(state.settings.uiScale)
  const panels = narrow ? { ...state.panels, browserOpen: false } : state.panels
  return createStore<AppState>()(() => ({ ...state, panels }))
}
